import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { IconClock, IconCheck } from '@tabler/icons-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import { Logo } from '../../components/app/Logo';
import { AppEntryButton } from '../../components/app/AppEntry';
import { Breadcrumbs } from '../../components/ui/Breadcrumbs';
import { DOCS_NAV, DOCS_NAV_FLAT, findDocGroup, findDocItem, type DocItem } from './docsNav';
import { DocsFeedbackModal } from './DocsFeedbackModal';

// ── TopBar ───────────────────────────────────────────────────────────────────
const TopBar: React.FC = () => (
  <header
    style={{
      position: 'sticky',
      top: 0,
      zIndex: 20,
      background: 'var(--bg)',
      backdropFilter: 'saturate(1.4) blur(10px)',
      borderBottom: '1px solid var(--line)',
    }}
  >
    <div
      style={{
        maxWidth: 1440,
        margin: '0 auto',
        padding: '14px 40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
      }}
    >
      <Logo subtitle="Документация" />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Link
          to="/policy"
          style={{ fontSize: 13, color: 'var(--muted)', padding: '8px 12px', borderRadius: 8 }}
        >
          Политика
        </Link>
        <AppEntryButton />
      </div>
    </div>
  </header>
);

// ── Side navigation ──────────────────────────────────────────────────────────
const SideNav: React.FC<{ activeId: string; onPick: (id: string) => void }> = ({
  activeId,
  onPick,
}) => (
  <nav
    style={{
      position: 'sticky',
      top: 72,
      maxHeight: 'calc(100vh - 72px)',
      overflowY: 'auto',
      padding: '24px 16px 24px 0',
    }}
  >
    {DOCS_NAV.map((g) => (
      <div key={g.group} style={{ marginBottom: 22 }}>
        <div
          style={{
            fontSize: 10,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--soft)',
            fontWeight: 600,
            padding: '0 12px 8px',
          }}
        >
          {g.group}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {g.items.map((it) => {
            const active = it.id === activeId;
            return (
              <button
                key={it.id}
                onClick={() => onPick(it.id)}
                className="docs-nav-item"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '7px 12px',
                  background: active ? 'var(--accent-soft)' : 'transparent',
                  color: active ? 'var(--accent-deep)' : 'var(--ink-2)',
                  border: 'none',
                  borderRadius: 7,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 13,
                  fontWeight: active ? 500 : 400,
                }}
              >
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {it.title}
                </span>
                {it.badge && (
                  <span
                    style={{
                      fontSize: 9,
                      padding: '1px 6px',
                      borderRadius: 999,
                      background: 'var(--warn-soft)',
                      color: 'var(--warn)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {it.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    ))}
  </nav>
);

// ── Right outline ────────────────────────────────────────────────────────────
const Outline: React.FC<{
  headings: { id: string; text: string }[];
  onReportClick: () => void;
}> = ({ headings, onReportClick }) => (
  <aside style={{ position: 'sticky', top: 72, paddingTop: 24 }}>
    {headings.length > 0 && (
      <>
        <div
          style={{
            fontSize: 10,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--soft)',
            fontWeight: 600,
            marginBottom: 10,
          }}
        >
          В этой статье
        </div>
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            borderLeft: '1px solid var(--line)',
          }}
        >
          {headings.map((h) => (
            <li key={h.id}>
              <a
                href={`#${h.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToHeading(h.id);
                }}
                className="docs-outline-link"
                style={{
                  display: 'block',
                  padding: '6px 0 6px 14px',
                  marginLeft: -1,
                  borderLeft: '2px solid transparent',
                  fontSize: 12,
                  color: 'var(--muted)',
                }}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </>
    )}
    <div
      style={{
        marginTop: 24,
        padding: '12px 14px',
        background: 'var(--accent-soft)',
        borderRadius: 10,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-deep)', marginBottom: 4 }}>
        Нашёл ошибку или есть идея?
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 8 }}>
        Напиши нам — фидбек прилетит команде на почту, разберёмся.
      </div>
      <button
        type="button"
        onClick={onReportClick}
        style={{
          fontSize: 11,
          color: 'var(--accent-deep)',
          fontWeight: 500,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        Сообщить нам →
      </button>
    </div>
  </aside>
);

// ── Feedback row ─────────────────────────────────────────────────────────────
const FeedbackRow: React.FC<{ onNotFound: () => void }> = ({ onNotFound }) => {
  const [voted, setVoted] = useState(false);
  const btnStyle: React.CSSProperties = {
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 500,
    background: 'var(--surface)',
    color: 'var(--ink-2)',
    border: '1px solid var(--line-strong)',
    borderRadius: 7,
    cursor: 'pointer',
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '14px 18px',
        background: 'var(--surface-2)',
        borderRadius: 10,
        border: '1px solid var(--line)',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Эта статья оказалась полезной?</span>
      {voted ? (
        <span
          style={{
            fontSize: 12,
            color: 'var(--accent-deep)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontWeight: 500,
          }}
        >
          <IconCheck size={14} /> Спасибо!
        </span>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setVoted(true)} style={btnStyle}>
            Да, полезная
          </button>
          <button onClick={onNotFound} style={btnStyle}>
            Не нашёл ответа
          </button>
        </div>
      )}
    </div>
  );
};

// ── Prev / next card ─────────────────────────────────────────────────────────
const NavCard: React.FC<{ item: DocItem; dir: 'prev' | 'next'; onPick: (id: string) => void }> = ({
  item,
  dir,
  onPick,
}) => (
  <a
    href={`/docs/${item.id}`}
    onClick={(e) => {
      e.preventDefault();
      onPick(item.id);
    }}
    className="docs-navcard"
    style={{
      display: 'block',
      padding: 16,
      borderRadius: 10,
      border: '1px solid var(--line)',
      background: 'var(--surface)',
    }}
  >
    <div
      style={{
        fontSize: 11,
        color: 'var(--muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
        fontWeight: 600,
        marginBottom: 6,
        textAlign: dir === 'prev' ? 'left' : 'right',
      }}
    >
      {dir === 'prev' ? '← Раньше' : 'Дальше →'}
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, textAlign: dir === 'prev' ? 'left' : 'right' }}>
      {item.title}
    </div>
  </a>
);

// ── Page ─────────────────────────────────────────────────────────────────────
// Раздел статьи открывается по ссылке вида /docs/<id>#<раздел>: адрес обновляем без нового шага
// в истории, чтобы «Назад» уводил на прошлую статью, а не на прошлый раздел.
const scrollToHeading = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  window.history.replaceState(window.history.state, '', `#${encodeURIComponent(id)}`);
};

// Таблицы в статьях бывают по 4–5 колонок: на телефоне прокручиваются внутри рамки, а не
// растягивают страницу.
const MARKDOWN_COMPONENTS: Components = {
  table: ({ node: _node, ...props }) => (
    <div className="docs-table-wrap">
      <table {...props} />
    </div>
  ),
  // Ссылки на другие статьи — переход внутри приложения, без перезагрузки страницы;
  // якоря — как в оглавлении; внешние — в новой вкладке.
  a: ({ node: _node, href = '', children, ...props }) => {
    if (href.startsWith('/docs')) {
      return (
        <Link to={href} onClick={() => window.scrollTo({ top: 0 })} {...props}>
          {children}
        </Link>
      );
    }
    if (href.startsWith('#')) {
      return (
        <a
          href={href}
          onClick={(e) => {
            e.preventDefault();
            scrollToHeading(decodeURIComponent(href.slice(1)));
          }}
          {...props}
        >
          {children}
        </a>
      );
    }
    const isExternal = /^https?:\/\//.test(href);
    return (
      <a
        href={href}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...props}
      >
        {children}
      </a>
    );
  },
};

export const Docs: React.FC = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const activeId = articleId && findDocItem(articleId) ? articleId : DOCS_NAV_FLAT[0].id;

  const item = findDocItem(activeId);
  const group = findDocGroup(activeId);
  const flatIdx = DOCS_NAV_FLAT.findIndex((i) => i.id === activeId);
  const prev = flatIdx > 0 ? DOCS_NAV_FLAT[flatIdx - 1] : null;
  const next = flatIdx < DOCS_NAV_FLAT.length - 1 ? DOCS_NAV_FLAT[flatIdx + 1] : null;

  const [markdown, setMarkdown] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');
  const [headings, setHeadings] = useState<{ id: string; text: string }[]>([]);
  const articleRef = useRef<HTMLDivElement>(null);

  // Fetch the Markdown file for the active article.
  useEffect(() => {
    let cancelled = false;
    setState('loading');
    setMarkdown(null);
    setHeadings([]);
    fetch(`/docs/${activeId}.md`)
      .then((res) => {
        if (!res.ok) throw new Error('not found');
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        // A missing file on a SPA host returns index.html — guard against it.
        if (text.trimStart().startsWith('<!doctype') || text.trimStart().startsWith('<!DOCTYPE')) {
          throw new Error('not found');
        }
        setMarkdown(text);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('missing');
      });
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  // Collect h2 headings for the right-hand outline once content is rendered.
  useEffect(() => {
    if (state !== 'ready') return;
    const nodes = articleRef.current?.querySelectorAll('h2[id]');
    if (!nodes) return;
    setHeadings(Array.from(nodes).map((n) => ({ id: n.id, text: n.textContent ?? '' })));
    // Открыли ссылку на раздел — прокрутить к нему, когда статья отрисовалась.
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (hash) document.getElementById(hash)?.scrollIntoView();
  }, [state, markdown]);

  const goTo = (id: string) => {
    navigate(`/docs/${id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const feedbackPageUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="docs-page" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Рендерится после общего PageTitle и перекрывает его: у каждой статьи свой заголовок вкладки. */}
      <Helmet>
        <title>
          {item
            ? `${item.title} — ${t('docs.title')} SkillScape`
            : `${t('docs.title')} — SkillScape`}
        </title>
      </Helmet>
      <style>{`
        .docs-page {
          --bg: #F8F9FA; --surface: #FFFFFF; --surface-2: #F1F3F5;
          --line: #E9ECEF; --line-strong: #DEE2E6;
          --ink: #212529; --ink-2: #495057; --muted: #868E96; --soft: #ADB5BD;
          --accent: #12B886; --accent-soft: #E6FCF5; --accent-deep: #099268;
          --warn: #F08C00; --warn-soft: #FFF4E6;
          color: var(--ink);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          font-size: 14px;
          -webkit-font-smoothing: antialiased;
        }
        [data-mantine-color-scheme='dark'] .docs-page {
          --bg: #1A1B1E; --surface: #25262B; --surface-2: #2C2E33;
          --line: #373A40; --line-strong: #5C5F66;
          --ink: #C1C2C5; --ink-2: #A6A7AB; --muted: #909296; --soft: #5C5F66;
          --accent: #20C997; --accent-soft: #0C3B30; --accent-deep: #63E6BE;
          --warn: #FFA94D; --warn-soft: #3B2A18;
        }
        .docs-page a:not(.mantine-Button-root) { color: inherit; text-decoration: none; }
        .docs-nav-item:hover { background: var(--surface-2) !important; }
        .docs-navcard { transition: border-color 120ms ease; }
        .docs-navcard:hover { border-color: var(--accent) !important; }
        .docs-outline-link:hover { color: var(--accent-deep) !important; border-left-color: var(--accent) !important; }
        .docs-prose p { font-size: 15px; line-height: 1.65; color: var(--ink-2); margin: 0 0 14px; }
        .docs-prose h2 { font-size: 22px; font-weight: 600; letter-spacing: -0.01em; margin: 32px 0 14px; scroll-margin-top: 100px; color: var(--ink); }
        .docs-prose h3 { font-size: 17px; font-weight: 600; margin: 22px 0 10px; color: var(--ink); }
        .docs-prose ul, .docs-prose ol { padding-left: 22px; margin: 0 0 14px; }
        .docs-prose li > ul, .docs-prose li > ol { margin: 4px 0 0; }
        .docs-prose li + li { margin-top: 4px; }
        .docs-prose li { font-size: 15px; line-height: 1.7; color: var(--ink-2); }
        .docs-prose code { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 13px; padding: 2px 6px; border-radius: 4px; background: var(--surface-2); color: var(--accent-deep); }
        .docs-prose pre { margin: 0 0 16px; padding: 14px 16px; border-radius: 8px; background: var(--surface-2); border: 1px solid var(--line); overflow-x: auto; }
        .docs-prose pre code { padding: 0; background: none; color: var(--ink); font-size: 13px; line-height: 1.6; }
        .docs-prose .docs-table-wrap { margin: 0 0 18px; overflow-x: auto; border: 1px solid var(--line); border-radius: 8px; }
        .docs-prose table { width: 100%; border-collapse: collapse; font-size: 14px; line-height: 1.5; }
        .docs-prose th, .docs-prose td { padding: 10px 14px; text-align: left; vertical-align: top; border-bottom: 1px solid var(--line); color: var(--ink-2); }
        .docs-prose th { background: var(--surface-2); color: var(--ink); font-weight: 600; font-size: 13px; white-space: nowrap; }
        .docs-prose td:first-child { color: var(--ink); font-weight: 500; }
        .docs-prose tr:last-child td { border-bottom: none; }
        .docs-prose td code, .docs-prose th code { white-space: nowrap; }
        .docs-prose strong { color: var(--ink); font-weight: 600; }
        .docs-prose a { color: var(--accent-deep); font-weight: 500; }
        .docs-prose a:hover { text-decoration: underline; }
      `}</style>

      <TopBar />

      <div
        style={{
          maxWidth: 1440,
          margin: '0 auto',
          padding: '0 40px',
          display: 'grid',
          gridTemplateColumns: '260px minmax(0, 1fr) 220px',
          gap: 40,
          alignItems: 'flex-start',
        }}
      >
        <SideNav activeId={activeId} onPick={goTo} />

        <article style={{ paddingTop: 24, paddingBottom: 80 }}>
          <Breadcrumbs
            size="sm"
            style={
              {
                marginBottom: 12,
                // /docs использует свою палитру через scoped CSS-vars
                // (--muted/--soft/--ink). Мапим их на bc-* переменные.
                '--bc-muted': 'var(--muted)',
                '--bc-current': 'var(--muted)',
                '--bc-soft': 'var(--soft)',
              } as React.CSSProperties
            }
            items={[{ label: 'Документация' }, { label: group ?? '' }]}
          />

          <h1
            style={{
              fontSize: 34,
              fontWeight: 700,
              letterSpacing: '-0.015em',
              lineHeight: 1.1,
              marginBottom: 12,
              color: 'var(--ink)',
            }}
          >
            {item?.title}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                border: '1px solid var(--line-strong)',
                background: 'var(--surface)',
                color: 'var(--ink-2)',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <IconClock size={12} /> {item?.time} чтения
            </span>
            {item?.badge && (
              <span
                style={{
                  fontSize: 11,
                  padding: '3px 9px',
                  borderRadius: 999,
                  background: 'var(--warn-soft)',
                  color: 'var(--warn)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {item.badge}
              </span>
            )}
          </div>

          <div className="docs-prose" ref={articleRef}>
            {state === 'loading' && <p>Загрузка…</p>}
            {state === 'missing' && (
              <>
                <p>
                  Эта статья сейчас в работе. Текст документации хранится в Markdown-файлах
                  репозитория — статью можно добавить через merge request.
                </p>
                <p>Пока загляни в смежные темы через боковую навигацию слева.</p>
              </>
            )}
            {state === 'ready' && markdown && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSlug]}
                components={MARKDOWN_COMPONENTS}
              >
                {markdown}
              </ReactMarkdown>
            )}
          </div>

          <div style={{ marginTop: 56, paddingTop: 28, borderTop: '1px solid var(--line)' }}>
            <FeedbackRow onNotFound={() => setIsFeedbackOpen(true)} />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
                marginTop: 24,
              }}
            >
              {prev ? <NavCard item={prev} dir="prev" onPick={goTo} /> : <div />}
              {next ? <NavCard item={next} dir="next" onPick={goTo} /> : <div />}
            </div>
          </div>
        </article>

        <Outline headings={headings} onReportClick={() => setIsFeedbackOpen(true)} />
      </div>

      <DocsFeedbackModal
        opened={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        pageUrl={feedbackPageUrl}
        pageTitle={item?.title}
      />
    </div>
  );
};

export default Docs;
